import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import TrophySelector from './TrophySelector';
import CarrotCatchGameTest from './CarrotCatchGameTest';

const StudySessionEditor = ({ session, onBack, isNew }) => {
  const [formData, setFormData] = useState(session || {
    session_id: '',
    title: '',
    description: '',
    passage_reference: '',
    lesson_ids: [],
    minigame_verses: [],
    bible_version: 'd6e14a625393b4da-01',
    trophy_config: {
      completion: { design: 'liberty', color: 'bronze', nameplate_text: 'Session Complete!', font_size: 0.75 },
      quiz_out: { design: 'sword', color: 'silver', nameplate_text: 'Quiz Out!', font_size: 0.75 },
      round_win: { design: 'shield', color: 'gold', nameplate_text: 'Round Winner!', font_size: 0.75 },
      bowl_win: { design: 'cup', color: 'gold', nameplate_text: 'Bowl Champion!', font_size: 0.75 }
    }
  });
  const [saving, setSaving] = useState(false);
  const [book, setBook] = useState('');
  const [chapter, setChapter] = useState('');
  const [availableVerses, setAvailableVerses] = useState([]);
  const [loadingVerses, setLoadingVerses] = useState(false);
  const [selectedVerses, setSelectedVerses] = useState(session?.minigame_verses || []);
  const [showTestGame, setShowTestGame] = useState(false);
  const [testVerseIndex, setTestVerseIndex] = useState(0); // Track which verse to test
  const [gameStarted, setGameStarted] = useState(false); // Track if game has started
  const { toast } = useToast();

  const handleSave = async () => {
    // Validation
    if (!formData.session_id || !formData.title || !formData.passage_reference) {
      toast({
        title: "Validation Error",
        description: "Session ID, Title, and Passage Reference are required",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');

      if (isNew) {
        await api.post('/admin/study-sessions', formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        toast({
          title: "Success",
          description: "Study session created successfully"
        });
      } else {
        await api.put(`/admin/study-sessions/${session.session_id}`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        toast({
          title: "Success",
          description: "Study session updated successfully"
        });
      }
      
      onBack();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save study session",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleTrophyUpdate = (trophyType, config) => {
    setFormData({
      ...formData,
      trophy_config: {
        ...formData.trophy_config,
        [trophyType]: config
      }
    });
  };

  const fetchChapterVerses = async () => {
    if (!book || !chapter) {
      toast({
        title: "Validation Error",
        description: "Please enter both book and chapter",
        variant: "destructive"
      });
      return;
    }

    setLoadingVerses(true);
    try {
      const response = await api.post('/minigame/verses/chapter', {
        book: book.toUpperCase(),
        chapter: parseInt(chapter),
        bible_version: formData.bible_version
      });
      
      setAvailableVerses(response.data.verses);
      // Clear previous selections when fetching new verses
      setSelectedVerses([]);
      toast({
        title: "Success",
        description: `Loaded ${response.data.verses.length} verses from ${response.data.chapter}. Previous selections cleared.`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to fetch verses",
        variant: "destructive"
      });
      setAvailableVerses([]);
    } finally {
      setLoadingVerses(false);
    }
  };

  const handleVerseToggle = (verseId) => {
    if (selectedVerses.includes(verseId)) {
      setSelectedVerses(selectedVerses.filter(v => v !== verseId));
    } else {
      if (selectedVerses.length >= 3) {
        toast({
          title: "Maximum Reached",
          description: "You can only select 3 verses for the mini-game",
          variant: "destructive"
        });
        return;
      }
      setSelectedVerses([...selectedVerses, verseId]);
    }
  };

  const saveMinigameVerses = async () => {
    if (selectedVerses.length !== 3) {
      toast({
        title: "Validation Error",
        description: "Please select exactly 3 verses for the mini-game",
        variant: "destructive"
      });
      return;
    }

    if (isNew) {
      // If creating new session, just update form data
      setFormData({
        ...formData,
        minigame_verses: selectedVerses
      });
      toast({
        title: "Success",
        description: "Mini-game verses will be saved with the session"
      });
      return;
    }

    // For existing sessions, save immediately
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await api.put(`/admin/study-sessions/${session.session_id}/minigame-verses`, {
        minigame_verses: selectedVerses,
        bible_version: formData.bible_version
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast({
        title: "Success",
        description: "Mini-game verses saved successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save mini-game verses",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            {isNew ? 'New Study Session' : `Edit ${session?.title}`}
          </h1>
          <Button onClick={onBack} variant="outline">
            Back
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Session Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="session_id">Session ID *</Label>
                <Input
                  id="session_id"
                  value={formData.session_id}
                  onChange={(e) => handleFieldChange('session_id', e.target.value)}
                  placeholder="e.g., eph5_session, john3_session"
                  disabled={!isNew}
                />
                <p className="text-xs text-gray-500">Unique identifier for this session (cannot be changed after creation)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  placeholder="e.g., Ephesians 5 - Living in the Light"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="passage_reference">Passage Reference *</Label>
                <Input
                  id="passage_reference"
                  value={formData.passage_reference}
                  onChange={(e) => handleFieldChange('passage_reference', e.target.value)}
                  placeholder="e.g., Ephesians 5:1-33"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Brief description of this study session"
                  rows={4}
                />
              </div>

              {!isNew && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-900">
                    <strong>Note:</strong> To manage lessons for this session (assign, reorder), 
                    use the "Manage Lessons" button from the sessions list.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-2">
              <Button onClick={handleSave} disabled={saving} className="flex-1">
                {saving ? 'Saving...' : 'Save Session'}
              </Button>
              <Button onClick={onBack} variant="outline">
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Mini-Game Configuration Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>🎮 Mini-Game Configuration</CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Select 3 verses for the Carrot Catch mini-game. Players will catch falling words in order to complete the verse.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Book and Chapter Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="book">Book Code</Label>
                  <Input
                    id="book"
                    value={book}
                    onChange={(e) => setBook(e.target.value)}
                    placeholder="e.g., GEN, EPH, JHN"
                  />
                  <p className="text-xs text-gray-500">3-letter book code (GEN, EXO, EPH, etc.)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="chapter">Chapter</Label>
                  <Input
                    id="chapter"
                    type="number"
                    value={chapter}
                    onChange={(e) => setChapter(e.target.value)}
                    placeholder="e.g., 1, 2, 3"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={fetchChapterVerses} 
                  disabled={loadingVerses}
                  variant="outline"
                  className="flex-1"
                >
                  {loadingVerses ? 'Loading Verses...' : 'Fetch Chapter Verses'}
                </Button>
                {selectedVerses.length > 0 && (
                  <Button 
                    onClick={() => setSelectedVerses([])} 
                    variant="destructive"
                    className="px-6"
                  >
                    Clear Selection
                  </Button>
                )}
              </div>

              {/* Verse Selection */}
              {availableVerses.length > 0 && (
                <div className="space-y-2">
                  <Label>Select Exactly 3 Verses ({selectedVerses.length}/3 selected)</Label>
                  <div className="max-h-64 overflow-y-auto border rounded-lg p-2 space-y-1">
                    {availableVerses.map((verse) => (
                      <div
                        key={verse.id}
                        onClick={() => handleVerseToggle(verse.id)}
                        className={`p-3 rounded cursor-pointer transition-colors ${
                          selectedVerses.includes(verse.id)
                            ? 'bg-green-100 border-2 border-green-500'
                            : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{verse.reference}</span>
                          {selectedVerses.includes(verse.id) && (
                            <span className="text-green-600 font-bold">✓</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Verses Summary */}
              {selectedVerses.length > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-900 mb-2">Selected Verses:</p>
                  <div className="space-y-1">
                    {selectedVerses.map((verseId, index) => {
                      const verse = availableVerses.find(v => v.id === verseId);
                      return (
                        <div key={verseId} className="text-sm text-blue-800">
                          {index + 1}. {verse?.reference || verseId}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedVerses.length === 3 && (
                <div className="space-y-2">
                  <Button 
                    onClick={saveMinigameVerses}
                    disabled={saving}
                    className="w-full"
                  >
                    {saving ? 'Saving...' : 'Save Mini-Game Verses'}
                  </Button>
                  <Button 
                    onClick={() => setShowTestGame(true)}
                    variant="outline"
                    className="w-full bg-blue-50 hover:bg-blue-100"
                  >
                    🎮 Test Mini-Game with Selected Verses
                  </Button>
                </div>
              )}

              {selectedVerses.length !== 3 && selectedVerses.length > 0 && (
                <p className="text-sm text-amber-600 text-center">
                  Please select {3 - selectedVerses.length} more verse(s) to enable saving
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Trophy Configuration Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Trophy Configuration</CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Assign trophies for different achievements in this session
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <TrophySelector
              trophyType="completion"
              trophyConfig={formData.trophy_config?.completion}
              onUpdate={(config) => handleTrophyUpdate('completion', config)}
              title="Session Completion Trophy"
              description="Awarded when all lessons reach 450+ points (Bronze only)"
              allowedColors={['bronze']}
            />

            <TrophySelector
              trophyType="quiz_out"
              trophyConfig={formData.trophy_config?.quiz_out}
              onUpdate={(config) => handleTrophyUpdate('quiz_out', config)}
              title="Quiz-Out Trophy"
              description="Awarded for quiz-out achievements (Silver only)"
              allowedColors={['silver']}
            />

            <TrophySelector
              trophyType="round_win"
              trophyConfig={formData.trophy_config?.round_win}
              onUpdate={(config) => handleTrophyUpdate('round_win', config)}
              title="Round Win Trophy"
              description="Awarded for winning a round (Gold only)"
              allowedColors={['gold']}
            />

            <TrophySelector
              trophyType="bowl_win"
              trophyConfig={formData.trophy_config?.bowl_win}
              onUpdate={(config) => handleTrophyUpdate('bowl_win', config)}
              title="Bowl Win Trophy"
              description="Awarded for winning the entire quiz bowl (Gold only)"
              allowedColors={['gold']}
            />

            <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-900">
                <strong>💡 Tip:</strong> Trophy colors are fixed by achievement type to maintain consistency. 
                Carrot Basket trophies are only available in Gold and Silver variants.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Test Game Modal with Verse Selector */}
      {showTestGame && selectedVerses.length === 3 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-3xl w-full my-8">
            {!gameStarted ? (
              /* Verse Selection Screen */
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-2">Select Verse to Test</h3>
                <p className="text-gray-600 mb-6">Choose which verse you want to test in the mini-game</p>
                
                <div className="space-y-3 mb-6">
                  {selectedVerses.map((verseId, index) => {
                    // Extract verse reference from verseId (e.g., "EPH.4.6" -> "Verse 6")
                    const parts = verseId.split('.');
                    const book = parts[0];
                    const chapter = parts[1];
                    const verseNum = parts[2];
                    const isActive = index === testVerseIndex;
                    
                    return (
                      <button
                        key={verseId}
                        onClick={() => setTestVerseIndex(index)}
                        className={`w-full p-4 rounded-lg font-medium transition-colors text-left ${
                          isActive 
                            ? 'bg-blue-600 text-white border-2 border-blue-600' 
                            : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm opacity-75 mb-1">Verse {index + 1}</div>
                            <div className="text-lg font-bold">{book} {chapter}:{verseNum}</div>
                          </div>
                          {isActive && (
                            <div className="text-2xl">✓</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => setGameStarted(true)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
                  >
                    ▶️ Start Testing Selected Verse
                  </Button>
                  <Button
                    onClick={() => {
                      setShowTestGame(false);
                      setTestVerseIndex(0);
                      setGameStarted(false);
                    }}
                    variant="outline"
                    className="px-6 py-6"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              /* Game Component */
              <>
                {/* Verse Info Header */}
                <div className="p-4 border-b border-gray-200 bg-blue-50 flex items-center justify-between">
                  <div>
                    <span className="text-sm text-gray-600">Testing:</span>
                    <span className="ml-2 font-bold text-blue-900">
                      Verse #{testVerseIndex + 1} ({selectedVerses[testVerseIndex].split('.').slice(0, 3).join(' ').replace('.', ':')})
                    </span>
                  </div>
                  <Button
                    onClick={() => {
                      setGameStarted(false);
                    }}
                    variant="outline"
                    size="sm"
                  >
                    ← Back to Verse Selection
                  </Button>
                </div>
                
                <CarrotCatchGameTest
                  verseId={selectedVerses[testVerseIndex]}
                  onClose={() => {
                    setShowTestGame(false);
                    setTestVerseIndex(0);
                    setGameStarted(false);
                  }}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudySessionEditor;
